<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import workerService from "@/modules/worker/services/workerService";

import type { WorkerAskedChangesResponse } from "@/modules/worker/components/workerAskChanges/types";
import type { WorkerDetail } from "@/modules/worker/types";

import Loader from "@/dsComponents/Loader.vue";
import SideModal from "@/dsComponents/modals/SideModal.vue";

import { useWorkerAskedChangesDisplay } from "@/modules/worker/components/workerAskChanges/useWorkerAskedChangesDisplay";
import { fetchWorkerAskedChanges } from "@/modules/worker/components/workerAskChanges/workerAskedChangesService";

import ValidateBlockItem from "./ValidateBlockItem.vue";
import ValidateJobsItems from "./ValidateJobsItems.vue";

const { t } = useI18n();

interface ModalAskedChangesProps {
    worker?: WorkerDetail | null;
    workerId: string;
    open: boolean;
}

const emit = defineEmits<(e: "close", refreshWorker: boolean) => void>();

const props = withDefaults(defineProps<ModalAskedChangesProps>(), {
    worker: null,
});

const refreshWorker = ref(false);
const loading = ref(false);
const loadError = ref(false);
const askedChangesResponse = ref<WorkerAskedChangesResponse | null>(null);
const resolvedWorker = ref<WorkerDetail | null>(props.worker);

watch(
    () => props.worker,
    (next) => {
        if (next) {
            resolvedWorker.value = next;
        }
    }
);

watch(
    () => [props.open, props.workerId, props.worker] as const,
    async ([isOpen, workerId, workerProp]) => {
        if (!isOpen || !workerId) return;
        if (workerProp) {
            resolvedWorker.value = workerProp;
            return;
        }
        try {
            resolvedWorker.value = await workerService.getWorker(workerId);
        } catch {
            resolvedWorker.value = null;
        }
    },
    { immediate: true }
);

const { askedChangesApiToDisplayItems } = useWorkerAskedChangesDisplay();

const displayItems = computed(() => {
    const w = resolvedWorker.value;
    if (!w || !askedChangesResponse.value) {
        return [];
    }
    return askedChangesApiToDisplayItems(w, askedChangesResponse.value);
});

const askedJobsChanges = computed(() => displayItems.value.filter((entry) => entry.field === "jobs"));
const askedOtherChanges = computed(() => displayItems.value.filter((entry) => entry.field !== "jobs"));

const loadAskedChanges = async () => {
    if (!props.workerId) return;
    loading.value = true;
    loadError.value = false;
    try {
        askedChangesResponse.value = await fetchWorkerAskedChanges(props.workerId);
    } catch {
        loadError.value = true;
        askedChangesResponse.value = null;
    } finally {
        loading.value = false;
    }
};

const handleValidatedItem = () => {
    refreshWorker.value = true;
    void loadAskedChanges();
};

watch(
    () => props.open,
    (isOpen) => {
        if (isOpen) {
            void loadAskedChanges();
        }
    },
    { immediate: true }
);
</script>
<template>
    <SideModal :open="open" @close="emit('close', refreshWorker)" :width="480" :title="t('page.worker.askedChanges.title')">
        <div v-if="loading" class="worker-asked-changes-modal__state">
            <Loader size="md" />
        </div>
        <p v-else-if="loadError" class="worker-asked-changes-modal__state typo-sm-regular">
            {{ t("page.worker.askedChanges.loadError") }}
        </p>
        <div v-else class="item-container">
            <template v-if="!resolvedWorker">
                <p class="worker-asked-changes-modal__state typo-sm-regular">
                    {{ t("page.worker.askedChanges.loadError") }}
                </p>
            </template>
            <template v-else-if="!displayItems.length">
                <p class="worker-asked-changes-modal__state typo-sm-regular">
                    {{ t("page.worker.askedChanges.empty") }}
                </p>
            </template>
            <template v-else>
                <ValidateBlockItem
                    v-for="(item, index) in askedOtherChanges"
                    :key="`${item.field}-${index}-${item.oldValue}-${item.newValue}`"
                    :item="item"
                    :separator="index !== 0"
                    :worker-id="workerId"
                    @validated-item="handleValidatedItem"
                />
                <ValidateJobsItems
                    v-if="askedJobsChanges.length"
                    :asked-jobs-changes="askedJobsChanges"
                    :separator="askedOtherChanges.length !== 0"
                    :worker-id="workerId"
                    @validated-item="handleValidatedItem"
                />
            </template>
        </div>
    </SideModal>
</template>
<style lang="scss" scoped>
.item-container {
    display: flex;
    flex-direction: column;
    gap: 28px;
}

.worker-asked-changes-modal__state {
    display: flex;
    justify-content: center;
    padding: var(--spacing-xl, 16px) 0;
    color: var(--text-sub-600, #494e61);
}
</style>
